/**
 * The "Needs you" badge: how many approval requests wait for the signed-in
 * user (GET /api/approvals/count — pending + failed, owner-only). Drives the
 * count on the Header's Needs-you link (APR-05).
 *
 * Refreshes every three minutes, when the tab becomes visible again, and at once
 * when the queue page answers a request (`notifyApprovalsChanged()`), so the
 * badge never lags the list the user is looking at.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';

export const APPROVALS_CHANGED_EVENT = 'compass:approvals-changed';
/** A backstop only: the visibility refresh and notifyApprovalsChanged() keep the badge current. */
export const APPROVAL_COUNT_POLL_MS = 3 * 60 * 1000;

/** Tell every badge that the queue changed (an edit, an answer, a request that turned out to be gone). */
export function notifyApprovalsChanged(): void {
  window.dispatchEvent(new Event(APPROVALS_CHANGED_EVENT));
}

/** @param enabled `false` while signed out (the API answers 401). */
export function useApprovalCount(enabled = true): number {
  const [count, setCount] = useState(0);

  const check = useCallback(async () => {
    try {
      setCount(await api.getApprovalCount());
    } catch {
      // non-blocking — the badge just stays stale until the next tick
    }
  }, []);

  useEffect(() => {
    if (!enabled) { setCount(0); return; }
    void check();
    const timer = setInterval(() => { if (document.visibilityState !== 'hidden') void check(); }, APPROVAL_COUNT_POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') void check(); };
    const onChanged = () => { void check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(APPROVALS_CHANGED_EVENT, onChanged);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(APPROVALS_CHANGED_EVENT, onChanged);
    };
  }, [enabled, check]);

  return count;
}
